import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils/cn";
import { CloseIcon } from "@/components/icons";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { apiClient } from "@/services/apiClient";
import { InlineVideoPlayer } from "@/components/ui/InlineVideoPlayer";
import { WebsiteTagsEditor, WebsiteTagObject } from "./WebsiteTagsEditor";

interface VideoPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaItem: MediaItem | null;
  clientId: string;
}

const STAGE_COLORS: Record<string, string> = {
  draft: "bg-gray-100 text-gray-600",
  dump: "bg-gray-100 text-gray-600",
  ready: "bg-blue-100 text-blue-700",
  next: "bg-amber-100 text-amber-700",
  published: "bg-green-100 text-green-700",
  pending_approval: "bg-purple-100 text-purple-700",
  approved: "bg-green-100 text-green-700",
  discarded: "bg-red-100 text-red-700",
};

export const VideoPreviewModal: React.FC<VideoPreviewModalProps> = ({
  isOpen,
  onClose,
  mediaItem,
  clientId,
}) => {
  const [tags, setTags] = useState<WebsiteTagObject[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved">(
    "idle"
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestTagsRef = useRef<WebsiteTagObject[]>([]);

  const [comment, setComment] = useState("");
  const [commentSaveStatus, setCommentSaveStatus] = useState<
    "idle" | "saving" | "saved"
  >("idle");
  const commentDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestCommentRef = useRef<string>("");

  useEffect(() => {
    if (isOpen && mediaItem) {
      const initial = mediaItem.websiteTags;
      setTags(initial);
      latestTagsRef.current = initial;
      setSaveStatus("idle");

      const initialComment = mediaItem.clientComment || "";
      setComment(initialComment);
      latestCommentRef.current = initialComment;
      setCommentSaveStatus("idle");
    }
  }, [isOpen, mediaItem?.id]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      if (commentDebounceRef.current) {
        clearTimeout(commentDebounceRef.current);
        commentDebounceRef.current = null;
      }
    };
  }, []);

  const persistTags = useCallback(
    async (nextTags: WebsiteTagObject[]) => {
      if (!mediaItem) return;
      setIsSaving(true);
      setSaveStatus("saving");
      try {
        await apiClient.patch(
          `/client/${clientId}/media/${mediaItem.id}/website-tags`,
          { tags: nextTags }
        );
        setSaveStatus("saved");
      } catch (err) {
        console.error("Failed to save website tags", err);
        setSaveStatus("idle");
      } finally {
        setIsSaving(false);
      }
    },
    [clientId, mediaItem]
  );

  const handleTagsChange = useCallback(
    (nextTags: WebsiteTagObject[]) => {
      setTags(nextTags);
      latestTagsRef.current = nextTags;
      setSaveStatus("idle");

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      debounceRef.current = setTimeout(() => {
        persistTags(latestTagsRef.current);
      }, 500);
    },
    [persistTags]
  );

  const persistComment = useCallback(
    async (nextComment: string) => {
      if (!mediaItem) return;
      setCommentSaveStatus("saving");
      try {
        await apiClient.patch(`/admin/media/${mediaItem.id}`, {
          clientId: clientId,
          client_comment: nextComment,
        });
        setCommentSaveStatus("saved");
      } catch (err) {
        console.error("Failed to save client comment", err);
        setCommentSaveStatus("idle");
      }
    },
    [clientId, mediaItem]
  );

  const handleCommentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const next = e.target.value;
      setComment(next);
      latestCommentRef.current = next;
      setCommentSaveStatus("idle");

      if (commentDebounceRef.current) {
        clearTimeout(commentDebounceRef.current);
      }
      commentDebounceRef.current = setTimeout(() => {
        persistComment(latestCommentRef.current);
      }, 500);
    },
    [persistComment]
  );

  if (!isOpen || !mediaItem) return null;

  const stage = mediaItem.stage || "draft";
  const stageColor = STAGE_COLORS[stage] || STAGE_COLORS.draft;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-xs"
        onClick={onClose}
      />

      <div
        className={cn(
          "relative bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden",
          "w-[95vw] h-[90vh] max-w-6xl",
          "animate-in fade-in-0 zoom-in-95 duration-200"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-3 border-b bg-gray-50">
          <div className="flex items-center gap-3 min-w-0">
            <h2 className="text-sm font-medium text-gray-900 truncate max-w-[300px]">
              {mediaItem.originalFilename}
            </h2>
            <span
              className={cn(
                "px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap",
                stageColor
              )}
            >
              {stage}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
          >
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="flex-1 bg-black flex items-center justify-center p-4">
            <div
              className="h-full max-h-full"
              style={{ aspectRatio: "9 / 16" }}
            >
              <InlineVideoPlayer
                mediaItem={mediaItem}
                autoPlay={false}
                objectFit="contain"
              />
            </div>
          </div>

          <div className="w-72 md:w-80 border-l bg-white flex flex-col">
            <div className="p-5 space-y-6 overflow-y-auto flex-1">
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label
                    htmlFor="media-client-comment"
                    className="block text-xs font-medium text-gray-700"
                  >
                    Client comment
                  </label>
                  {commentSaveStatus === "saving" && (
                    <span className="text-[10px] text-gray-400">Saving...</span>
                  )}
                  {commentSaveStatus === "saved" && (
                    <span className="text-[10px] text-green-600">Saved</span>
                  )}
                </div>
                <textarea
                  id="media-client-comment"
                  value={comment}
                  onChange={handleCommentChange}
                  placeholder="Add a comment..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              <hr className="border-gray-200" />

              <WebsiteTagsEditor
                tags={tags}
                onTagsChange={handleTagsChange}
                isSaving={isSaving}
                saveStatus={saveStatus}
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
